import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { impersonateLogout } from 'actions/auth';
import './ImpersonationBanner.scss';

/**
 * ImpersonationBanner displays a small draggable floating panel when viewing the site as an impersonated user.
 * Shows the user email and provides an "End" button to log out.
 */
const ImpersonationBanner = () => {
    const dispatch = useDispatch();
    const { isImpersonated, user } = useSelector(state => state.auth);
    const [position, setPosition] = useState({ x: 20, y: 20 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const panelRef = useRef(null);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging || !panelRef.current) return;

            const panel = panelRef.current;
            const panelRect = panel.getBoundingClientRect();
            const padding = 10;

            // Calculate bounds
            const minX = padding;
            const minY = padding;
            const maxX = window.innerWidth - panelRect.width - padding;
            const maxY = window.innerHeight - panelRect.height - padding;

            // Clamp position within bounds
            const newX = Math.min(Math.max(e.clientX - dragOffset.x, minX), maxX);
            const newY = Math.min(Math.max(e.clientY - dragOffset.y, minY), maxY);

            setPosition({ x: newX, y: newY });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);

    if (!isImpersonated || !user) {
        return null;
    }

    const handleMouseDown = (e) => {
        if (e.target.tagName === 'BUTTON') return; // Don't drag when clicking button
        const rect = panelRef.current.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
        setIsDragging(true);
    };

    const handleEndSession = () => {
        dispatch(impersonateLogout());
        window.close();
        window.location.href = '/login';
    };

    return (
        <div
            ref={panelRef}
            className={`impersonation-panel ${isDragging ? 'dragging' : ''}`}
            style={{ left: position.x, top: position.y }}
            onMouseDown={handleMouseDown}
        >
            <div className="impersonation-panel__icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                </svg>
            </div>
            <div className="impersonation-panel__info">
                <span className="impersonation-panel__label">Impersonating</span>
                <span className="impersonation-panel__email">{user.email}</span>
            </div>
            <button
                className="impersonation-panel__button"
                onClick={handleEndSession}
            >
                End
            </button>
        </div>
    );
};

export default ImpersonationBanner;
